import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Calendar, MapPin, Building, FileText, Shield, Clock, Users, Loader2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_flats-clean-service/artifacts/2b1a8yim_Screenshot_20260123_012606_Drive.jpg";

const SCOPE_LABELS = {
  stairwells_landings: 'Stairwells & landings',
  communal_corridors: 'Communal corridors',
  entrances_lobbies: 'Entrances & lobbies',
  lifts: 'Lifts',
  bin_areas: 'Bin areas',
  cobweb_high_level_dusting: 'Cobweb & high-level dusting',
  glass_internal_doors: 'Glass & internal entrance doors',
  external_litter_grounds: 'External litter picking / grounds',
};

const PublicQuote = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quotes/${quoteId}`);
      if (response.ok) {
        const data = await response.json();
        setQuote(data.quote);
      } else {
        setError('Quote not found or has expired.');
      }
    } catch (err) {
      console.error('Error fetching quote:', err);
      setError('Unable to load quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      // Use the endpoint with email notifications
      const response = await fetch(`${API_URL}/api/quotes/${quoteId}/accept-with-notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        navigate(`/quote/${quoteId}/accepted`);
      } else {
        setError('Unable to accept quote. Please try again or contact us.');
      }
    } catch (err) {
      console.error('Error accepting quote:', err);
      setError('Connection error. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getSelectedScope = () => {
    if (!quote?.scope_of_works) return [];
    return Object.entries(quote.scope_of_works)
      .filter(([_, selected]) => selected)
      .map(([key, _]) => SCOPE_LABELS[key] || key);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1f4e]"></div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Quote Not Available</h1>
          <p className="text-gray-500">{error || 'This quote may have expired or been removed.'}</p>
        </div>
      </div>
    );
  }

  const isExpired = new Date(quote.expires_at) < new Date();
  const isAccepted = quote.status === 'accepted';
  
  // Handle legacy quotes that might have site_location instead of site_address
  const siteAddress = quote.site_address || quote.site_location || '';

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* 1. HEADER */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-5 py-6 text-center">
          <img src={LOGO_URL} alt="Just Cleaned" className="h-10 w-auto object-contain mx-auto mb-4" />
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">
            Your Communal Block Cleaning Quote
          </h1>
          <p className="text-gray-500 text-sm">
            Clear pricing • No obligation • Rolling monthly
          </p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8">
        
        {/* 2. CLIENT & SITE DETAILS */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Client</p>
              <p className="font-medium text-gray-900">{quote.company_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Quote ID</p>
              <p className="font-mono text-gray-700">{quote.id}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Site Address</p>
              <p className="font-medium text-gray-900">{siteAddress}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Blocks / Entrances</p>
              <p className="text-gray-700">{quote.number_of_blocks}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Cleaning Frequency</p>
              <p className="text-gray-700 capitalize">{quote.cleaning_frequency}</p>
            </div>
            <div className="col-span-2 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Quote Valid Until</p>
              <p className="text-gray-700">{formatDate(quote.expires_at)}</p>
            </div>
          </div>
        </section>

        {/* 3. PRICE SECTION - MOST PROMINENT */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-6 text-center">
          <p className="text-sm text-gray-500 mb-2">Monthly Price</p>
          <p className="text-5xl sm:text-6xl font-bold text-gray-900 mb-1">
            {formatPrice(quote.monthly_price)}
          </p>
          <p className="text-lg text-gray-600 mb-4">/ month</p>
          
          {/* VAT Status */}
          <p className="text-sm text-gray-600 mb-4">
            {quote.vat_registered 
              ? "+ VAT" 
              : "VAT not applicable (not VAT registered)"}
          </p>
          
          {/* Price Reframing */}
          <p className="text-sm text-gray-500 border-t border-gray-100 pt-4 max-w-md mx-auto">
            Price reflects consistent attendance, insured operatives, and a fixed cleaning rota — not one-off presentation cleans.
          </p>
        </section>

        {/* 4. SCOPE OF WORKS */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Scope of Works</h2>
          <div className="space-y-3">
            {getSelectedScope().map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-[#00c853] flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 5. TERMS */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Terms</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Contract type</span>
              <span className="font-medium text-gray-900">Rolling monthly</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Notice period</span>
              <span className="font-medium text-gray-900">{quote.notice_period} days</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Start timeframe</span>
              <span className="font-medium text-gray-900">{quote.start_timeframe}</span>
            </div>
            <div className="pt-3 border-t border-gray-100 space-y-2 text-sm text-gray-600">
              <p>• Pricing reviewed only if scope changes</p>
              <p>• No obligation until quote is accepted</p>
            </div>
          </div>
        </section>

        {/* 6. INCLUDED AS STANDARD */}
        <section className="bg-[#f0f4f8] rounded-xl p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Included as Standard</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { icon: Shield, text: 'Fully insured (Public, Employer\'s & Professional Indemnity)' },
              { icon: Calendar, text: 'Fixed rota – no missed cleans' },
              { icon: Users, text: 'One point of contact' },
              { icon: CheckCircle, text: 'No chasing required' },
              { icon: Check, text: 'Consistent standards maintained' },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                <item.icon className="w-4 h-4 text-[#1a1f4e] flex-shrink-0" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Notes (if any) */}
        {quote.notes && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Notes</h2>
            <p className="text-gray-600">{quote.notes}</p>
          </section>
        )}

        {/* 7 & 8. CTA SECTION */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center mb-6">
          {isAccepted ? (
            <div>
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Quote Accepted</h2>
              <p className="text-gray-500">Thank you — we'll be in touch shortly to arrange your start date.</p>
            </div>
          ) : isExpired ? (
            <div>
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Quote Expired</h2>
              <p className="text-gray-500">This quote has expired. Please contact us for an updated quote.</p>
            </div>
          ) : (
            <>
              {/* 7. PRIMARY CTA */}
              <Button
                onClick={handleAccept}
                disabled={accepting}
                className="w-full sm:w-auto bg-[#00c853] hover:bg-[#00a844] text-white h-14 px-12 text-lg font-medium rounded-xl shadow-[0_2px_8px_rgba(0,200,83,0.25)] hover:shadow-[0_4px_12px_rgba(0,200,83,0.35)]"
                data-testid="accept-quote-btn"
              >
                {accepting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Accept Quote'
                )}
              </Button>
              
              {/* 8. ACCEPTANCE FALLBACK */}
              <p className="text-sm text-gray-500 mt-4">
                You can also reply to this email with "Approved" to accept this quote.
              </p>
              
              {/* 9. POST-ACCEPTANCE REASSURANCE */}
              <p className="text-xs text-gray-400 mt-3">
                Once accepted, we'll confirm your start date and cleaning rota by email.
              </p>
            </>
          )}
        </section>

        {/* 10. FOOTER */}
        <footer className="text-center py-6 border-t border-gray-200">
          <p className="text-gray-600 text-sm mb-2">
            Over 4 years' experience maintaining managed residential blocks across London.
          </p>
          <p className="text-gray-500 text-xs mb-4">
            Service area: North & Greater London
          </p>
          <div className="flex justify-center gap-4 text-xs text-gray-400">
            <Link to="/privacy-policy" target="_blank" className="hover:text-gray-600">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms-conditions" target="_blank" className="hover:text-gray-600">Terms & Conditions</Link>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default PublicQuote;
